/**
 * Pure op geometry helpers (Collab Phase 7). DOM-free imports only — usable
 * from op-capture, the rebase path, and tests without dragging in any module
 * graph.
 *
 * MIRROR CONTRACT: `deriveOpMetadata` must classify patches exactly like the
 * server's `deriveAnnotations` (ai/collab/collab-core.js) — the server
 * cross-checks the client's claims and rejects mismatches as `stale`. The
 * lock-key strings ('s:<instanceId>', 'shared:navigation', 'shared:footer')
 * are pinned by collab-protocol tests; change them everywhere or nowhere.
 */

import { classifyEditableIdRepairBatch } from '../shared/editable-id-repair.js';

const SECTION_PATH_RE = /^pages\.(\d+)\.sections\.(\d+)(?:\.|$)/;
const SECTIONS_ARRAY_PATH_RE = /^pages\.(\d+)\.sections$/;
const PAGE_PATH_RE = /^pages\.(\d+)(?:\.|$)/;

/**
 * Segment-wise path intersection: true when one path is the other (or a
 * segment-prefix of it). Array-op "poisoning" falls out naturally: an
 * insert/remove/move at 'pages.0.sections' intersects EVERY
 * 'pages.0.sections.<j>...' path because the array path is their prefix.
 */
export function pathsIntersect(pathA, pathB) {
  if (typeof pathA !== 'string' || typeof pathB !== 'string') return false;
  const a = pathA.split('.');
  const b = pathB.split('.');
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/** True when ANY patch in `patchesA` touches ANY patch in `patchesB`. */
export function patchesIntersect(patchesA, patchesB) {
  for (const a of patchesA || []) {
    for (const b of patchesB || []) {
      if (pathsIntersect(a?.path, b?.path)) return true;
    }
  }
  return false;
}

/**
 * Classify a patch list against the state the patches were AUTHORED AGAINST
 * (the pre-op state) — the client half of the server's annotation cross-check.
 * Returns `{sections: string[], pageId: string|'*', structural: boolean}` or
 * null when unresolvable (bad indices, missing instanceIds, page-spanning op).
 *
 * 16A2a: an insert/remove/move at a sections-array path derives the AFFECTED
 * section's lock key by simulating the op against a per-page id-list cursor —
 * remove at i names the section currently at i, move names the moved section,
 * insert names the inserted item. Before this, array patches contributed no
 * section keys, so the server's lock gate ran zero iterations for EVERY
 * structural op (delete/move/duplicate) — a peer's section lock never blocked
 * them. The cursor makes sequential patches (bulk delete's descending removes)
 * resolve exactly like applyPatches will. A stale index returns null, which
 * the server answers with a 'stale' rejection rather than a mis-annotation.
 *
 * Basis contract: the server calls this with the room state BEFORE applying
 * `forward`; the client's op-capture reconstructs the same pre-op basis from
 * the entry's reverse patches (see derivationBasis) because its sink fires
 * after the local mutation has landed.
 */
export function deriveOpMetadata(state, patches) {
  const repair = classifyEditableIdRepairBatch(state, patches);
  if (repair.kind === 'invalid') return null;
  if (repair.kind === 'candidate')
    return { sections: repair.sections, pageId: '*', structural: true, identityRepair: 1 };
  const sections = new Set();
  const pageIds = new Set();
  let structural = false;
  /** pageIndex -> working instanceId list, seeded lazily from `state`. */
  const cursors = new Map();
  for (const patch of patches || []) {
    const path = patch?.path;
    if (typeof path !== 'string') return null;
    if (path === 'pages') {
      structural = true;
      pageIds.add('*');
      continue;
    }
    const sectionsArrayMatch = path.match(SECTIONS_ARRAY_PATH_RE);
    if (sectionsArrayMatch) {
      const pageIndex = Number(sectionsArrayMatch[1]);
      const page = state?.pages?.[pageIndex];
      if (!page?.id) return null;
      structural = true;
      pageIds.add(page.id);
      const op = patch.op;
      if (op === 'insert' || op === 'remove' || op === 'move') {
        let ids = cursors.get(pageIndex);
        if (!ids) {
          ids = (page.sections || []).map((s) => s?.instanceId || null);
          cursors.set(pageIndex, ids);
        }
        if (op === 'insert') {
          const id = patch.item?.instanceId;
          if (!id) return null;
          if (!Number.isInteger(patch.index) || patch.index < 0 || patch.index > ids.length) {
            return null;
          }
          ids.splice(patch.index, 0, id);
          sections.add(`s:${id}`);
        } else if (op === 'remove') {
          if (!Number.isInteger(patch.index) || patch.index < 0 || patch.index >= ids.length) {
            return null;
          }
          const [id] = ids.splice(patch.index, 1);
          if (!id) return null;
          sections.add(`s:${id}`);
        } else {
          if (!Number.isInteger(patch.from) || patch.from < 0 || patch.from >= ids.length) {
            return null;
          }
          const [id] = ids.splice(patch.from, 1);
          if (!id) return null;
          if (!Number.isInteger(patch.to) || patch.to < 0 || patch.to > ids.length) {
            return null;
          }
          ids.splice(patch.to, 0, id);
          sections.add(`s:${id}`);
        }
      }
      continue;
    }
    const sectionMatch = path.match(SECTION_PATH_RE);
    if (sectionMatch) {
      const page = state?.pages?.[Number(sectionMatch[1])];
      const section = page?.sections?.[Number(sectionMatch[2])];
      if (!page?.id || !section?.instanceId) return null;
      pageIds.add(page.id);
      sections.add(`s:${section.instanceId}`);
      continue;
    }
    if (path === 'sharedSections.navigation' || path.startsWith('sharedSections.navigation.')) {
      sections.add('shared:navigation');
      pageIds.add('*');
      continue;
    }
    if (path === 'sharedSections.footer' || path.startsWith('sharedSections.footer.')) {
      sections.add('shared:footer');
      pageIds.add('*');
      continue;
    }
    const pageMatch = path.match(PAGE_PATH_RE);
    if (pageMatch) {
      const page = state?.pages?.[Number(pageMatch[1])];
      if (!page?.id) return null;
      pageIds.add(page.id);
      continue;
    }
    pageIds.add('*');
  }
  const concrete = [...pageIds].filter((p) => p !== '*');
  let pageId;
  if (concrete.length === 0) pageId = '*';
  else if (concrete.length === 1) pageId = concrete[0];
  else return null; // one op must not span pages
  return { sections: [...sections], pageId, structural };
}

function clampIndex(index, length) {
  return Math.max(0, Math.min(index, length));
}

/**
 * Conservative undo cut-below truncation (Collab Phase 9). When a remote op (or
 * a rejection rollback) touches paths that a LOCAL history entry also touches,
 * undoing past that entry would restore a value the remote no longer agrees
 * with. Rather than skip-holes, we:
 *   1. find the HIGHEST undo-stack index (0..position) whose patches intersect
 *      the remote paths and splice it AND everything older out (array ops poison
 *      everything under their array path via pathsIntersect's prefix rule);
 *   2. truncate the redo tail from the LOWEST future index that intersects.
 * Mutates `history.entries`/`history.position` in place. Returns true if it
 * changed anything (the caller emits a render-inert history-changed).
 */
export function trimHistoryAgainst(history, remotePatches) {
  const entries = history.entries;
  let position = history.position;
  const touches = (entry) =>
    patchesIntersect(entry?.forward, remotePatches) ||
    patchesIntersect(entry?.reverse, remotePatches);

  let cut = -1;
  for (let i = 0; i <= position && i < entries.length; i += 1) {
    if (touches(entries[i])) cut = i;
  }
  if (cut >= 0) {
    entries.splice(0, cut + 1);
    position -= cut + 1;
  }

  let truncateAt = -1;
  for (let i = position + 1; i < entries.length; i += 1) {
    if (touches(entries[i])) {
      truncateAt = i;
      break;
    }
  }
  if (truncateAt >= 0) entries.length = truncateAt;

  history.position = position;
  return cut >= 0 || truncateAt >= 0;
}

/**
 * Re-derive a patch list authored against `oldState` so it targets the same
 * LOGICAL entities (page ids + section instanceIds) in `newState` — the
 * client's recovery from a `stale` rejection after converging. Returns the
 * re-derived patch list, or null when any target vanished (the caller drops
 * the op, truncates its history entry, and toasts).
 */
export function rederivePatchesByInstance(patches, oldState, newState) {
  const out = [];
  for (const patch of patches || []) {
    const path = patch?.path;
    if (typeof path !== 'string') return null;

    const remapPagePrefix = (pageIndex) => {
      const pageId = oldState?.pages?.[pageIndex]?.id;
      if (!pageId) return -1;
      return (newState?.pages || []).findIndex((page) => page?.id === pageId);
    };

    const sectionMatch = path.match(SECTION_PATH_RE);
    if (sectionMatch) {
      const oldPageIndex = Number(sectionMatch[1]);
      const oldSectionIndex = Number(sectionMatch[2]);
      const newPageIndex = remapPagePrefix(oldPageIndex);
      if (newPageIndex < 0) return null;
      const instanceId = oldState?.pages?.[oldPageIndex]?.sections?.[oldSectionIndex]?.instanceId;
      if (!instanceId) return null;
      const newSectionIndex = (newState.pages[newPageIndex].sections || []).findIndex(
        (section) => section?.instanceId === instanceId
      );
      if (newSectionIndex < 0) return null; // section vanished
      const rest = path.slice(`pages.${oldPageIndex}.sections.${oldSectionIndex}`.length);
      out.push({ ...patch, path: `pages.${newPageIndex}.sections.${newSectionIndex}${rest}` });
      continue;
    }

    const sectionsArrayMatch = path.match(SECTIONS_ARRAY_PATH_RE);
    if (sectionsArrayMatch) {
      const oldPageIndex = Number(sectionsArrayMatch[1]);
      const newPageIndex = remapPagePrefix(oldPageIndex);
      if (newPageIndex < 0) return null;
      const newPath = `pages.${newPageIndex}.sections`;
      const oldSections = oldState?.pages?.[oldPageIndex]?.sections || [];
      const newSections = newState.pages[newPageIndex].sections || [];
      const findByOldIndex = (index) => {
        const instanceId = oldSections[index]?.instanceId;
        if (!instanceId) return -1;
        return newSections.findIndex((section) => section?.instanceId === instanceId);
      };
      if (patch.op === 'insert') {
        // Anchor to the (old) preceding sibling when it still exists; else
        // clamp the raw index into the new array.
        let index = clampIndex(patch.index, newSections.length);
        const anchor = findByOldIndex(patch.index - 1);
        if (patch.index > 0 && anchor >= 0) index = anchor + 1;
        out.push({ ...patch, path: newPath, index });
        continue;
      }
      if (patch.op === 'remove') {
        const index = findByOldIndex(patch.index);
        if (index < 0) return null; // already gone — nothing to remove
        out.push({ ...patch, path: newPath, index });
        continue;
      }
      if (patch.op === 'move') {
        const from = findByOldIndex(patch.from);
        if (from < 0) return null;
        out.push({
          ...patch,
          path: newPath,
          from,
          to: clampIndex(patch.to, newSections.length - 1)
        });
        continue;
      }
      out.push({ ...patch, path: newPath });
      continue;
    }

    const pageMatch = path.match(PAGE_PATH_RE);
    if (pageMatch) {
      const oldPageIndex = Number(pageMatch[1]);
      const newPageIndex = remapPagePrefix(oldPageIndex);
      if (newPageIndex < 0) return null;
      out.push({
        ...patch,
        path: `pages.${newPageIndex}${path.slice(`pages.${oldPageIndex}`.length)}`
      });
      continue;
    }

    // Global / pages-array / shared-section paths need no index remapping.
    out.push({ ...patch });
  }
  return out;
}
